import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Sparkles, X } from "lucide-react";
import TrackOnView from "./TrackOnView";
import { track } from "@/lib/posthog";
import { startProCheckout } from "@/lib/billing";
import { PROMO, type UpsellPlacement } from "@/lib/promo";
import { UPSELLS_ENABLED } from "@/lib/features";

const SAVE_PCT = Math.floor((1 - Number(PROMO.price) / Number(PROMO.regularPrice)) * 100);

interface Props {
  placement: UpsellPlacement;
  /** Shown when the user has run out (designs or refines). */
  title?: string;
  /** Eligible for the $1.99 intro month; otherwise shows standard Pro copy. */
  promoEligible: boolean;
  quotaRemaining?: number;
  dismissible?: boolean;
  className?: string;
}

/** Banner upsell for Pro. Tracks view / click / dismiss in PostHog. */
const PromoUpsell = ({ placement, title, promoEligible, quotaRemaining, dismissible, className = "" }: Props) => {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [hidden, setHidden] = useState(false);
  if (hidden) return null;

  const props = { placement, promo_eligible: promoEligible, quota_remaining: quotaRemaining, plan: "free" };
  const cta = promoEligible ? `Get Pro for ${SAVE_PCT}% off` : "Upgrade to Pro";

  const onClick = async () => {
    track("upsell_clicked", props);
    setBusy(true);
    try {
      window.open(await startProCheckout("month", "pro", placement), "_blank");
    } catch {
      navigate("/pricing?plan=pro&billing=monthly");
    } finally {
      setBusy(false);
    }
  };

  return (
    <TrackOnView event="upsell_viewed" properties={props} className={className}>
      <div className="relative rounded-2xl border-2 border-primary bg-background shadow-elegant overflow-hidden">
        <div className="bg-gradient-cta px-4 py-1.5 text-center text-[11px] font-semibold uppercase tracking-widest text-primary-foreground">
          {promoEligible ? "Limited time offer" : "Huelo Pro"}
        </div>
        <div className="px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5">
          <div className="flex-1 min-w-0 pr-6 sm:pr-0">
            {title && <p className="text-base font-semibold leading-snug text-foreground mb-1.5">{title}</p>}
            <div className="flex flex-wrap items-center gap-2">
              {promoEligible ? (
                <>
                  <span className="rounded-full border border-primary/40 bg-primary/10 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">
                    {SAVE_PCT}% off first month
                  </span>
                  <span className="text-sm text-muted-foreground line-through">${PROMO.regularPrice}</span>
                  <span className="text-xl font-bold text-foreground">${PROMO.price}</span>
                  <span className="text-xs text-muted-foreground">first month</span>
                </>
              ) : (
                <p className="text-sm text-foreground">Get Huelo Pro for ${PROMO.regularPrice}/month</p>
              )}
            </div>
          </div>
          <button
            onClick={onClick}
            disabled={busy}
            className="shrink-0 self-stretch sm:self-auto inline-flex items-center justify-center gap-2 rounded-full bg-gradient-cta text-primary-foreground px-5 py-2.5 text-sm font-semibold shadow-elegant hover:opacity-95 transition-opacity disabled:opacity-70 sm:mr-6"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            {cta}
          </button>
        </div>
        {dismissible && (
          <button
            aria-label="Dismiss"
            onClick={() => {
              track("upsell_dismissed", props);
              setHidden(true);
            }}
            className="absolute top-9 right-3 p-1 rounded-full text-muted-foreground hover:text-foreground"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </TrackOnView>
  );
};

const PromoUpsellGate = (p: Props) => (UPSELLS_ENABLED ? <PromoUpsell {...p} /> : null);
export default PromoUpsellGate;
